import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { PASSWORD_MIN } from '@pulsepoint/shared';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useSearchParams } from 'react-router';
import type { z } from 'zod';
import { useResetPassword } from '../../api/auth';
import { applyApiError } from '../../components/form/apiErrors';
import { PasswordField } from '../../components/form/PasswordField';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { choosePassword } from './passwordSchemas';

type Values = z.infer<typeof choosePassword>;

/** From the link in a reset or welcome email: /reset-password?token=… */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const form = useForm<Values>({ resolver: zodResolver(choosePassword), defaultValues: { password: '', confirm: '' } });

  // Don't leave the token in the address bar (or in browser history) once it's read.
  // It stays in memory for the submit.
  const [kept] = useState(token);
  useEffect(() => {
    if (token) window.history.replaceState(null, '', '/reset-password');
  }, [token]);

  if (!kept) {
    return (
      <AuthLayout title="This link is incomplete." lead="Open the link from your email again, or ask for a new one.">
        <Button component={RouterLink} to="/forgot-password" variant="contained">Ask for a new link</Button>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Your password is set." lead="You can sign in with it now.">
        <Button component={RouterLink} to="/sign-in" variant="contained">Sign in</Button>
      </AuthLayout>
    );
  }

  const submit = form.handleSubmit(async (values) => {
    setError('');
    try {
      await reset.mutateAsync({ token: kept, password: values.password });
      setDone(true);
    } catch (e) {
      setError(applyApiError(e, form.setError, ['password']));
    }
  });

  return (
    <AuthLayout title="Choose a password." lead={`Use at least ${PASSWORD_MIN} characters. A short sentence is easy to remember and hard to guess.`}>
      <Stack component="form" onSubmit={submit} noValidate spacing={2.5}>
        {/* Helps password managers save it against the right account. */}
        <input type="text" name="username" autoComplete="username" hidden readOnly />
        <PasswordField control={form.control} name="password" label="New password" autoComplete="new-password" onGenerate={(p) => { form.setValue('password', p, { shouldDirty: true, shouldValidate: true }); form.setValue('confirm', p, { shouldDirty: true, shouldValidate: true }); }} autoFocus />
        <PasswordField control={form.control} name="confirm" label="Type it again" autoComplete="new-password" />
        {error && (
          <Alert severity="error" role="alert">
            {error}{' '}
            {/expired|used/.test(error) && <Link component={RouterLink} to="/forgot-password">Ask for a new link</Link>}
          </Alert>
        )}
        <Button type="submit" variant="contained" disabled={reset.isPending} sx={{ minHeight: 48 }}>
          {reset.isPending ? 'Saving…' : 'Save the password'}
        </Button>
      </Stack>
    </AuthLayout>
  );
}
